import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';
import ImageWithSkeleton from '../components/ImageWithSkeleton';
import PhotoPlaceholder from '../components/PhotoPlaceholder';
import { APPLY_URL as APPLY_FORM_URL } from '../config';

// Static fallback shown when no live Buildium listings are available.
const fallbackPlans = [
  {
    id: 'fb-1',
    title: 'Studios',
    subtitle: 'Studio, One Bath',
    price: '$1,450',
    availability: 'Contact Us',
    image: 'https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/6un646s0_shoootin-photo-4-46a125f58792aa5cb7e1ce26b1a9011c.jpg',
  },
  {
    id: 'fb-2',
    title: '1 Bedroom',
    subtitle: 'One Bedroom, One Bath',
    price: '$1,200 - $1,500',
    availability: 'Aug 2026',
    image: 'https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/q72zkfgd_shoootin-photo-9-8c1f3e36e2d02b22c067719d5cab22cf.jpg',
  },
  {
    id: 'fb-3',
    title: '2 Bedroom 2 Bathroom',
    subtitle: '2 Bedroom, 2 Bath',
    price: '$1,100/bed',
    availability: 'Contact Us',
    image: 'https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/sbtik4uk_shoootin-photo-5-49d9f1232c43889349f19a195ba3ddd0.jpg',
  },
];

const formatMoney = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const n = Math.round(parseFloat(val));
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString('en-US')}`;
};

const formatRent = (listing) => {
  const range = listing.effectiveRent || listing.marketRent;
  if (!range) return 'Contact Us';
  const min = formatMoney(range.min);
  const max = formatMoney(range.max);
  if (!min) return 'Contact Us';
  return min === max ? `${min}/mo` : `${min} - ${max}/mo`;
};

const priceOf = (listing) => {
  const range = listing.effectiveRent || listing.marketRent;
  if (!range || !range.min) return null;
  const n = parseFloat(range.min);
  return Number.isNaN(n) ? null : n;
};

const bedFilterLabel = (n) => (n === 0 ? 'Studio' : `${n} Bed`);

const formatSqft = (listing) => {
  const sf = listing.squareFeet;
  if (!sf || !sf.min) return null;
  return sf.min === sf.max ? `${sf.min} sq ft` : `${sf.min}-${sf.max} sq ft`;
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  if (d <= now) return 'Available Now';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const bedBathLabel = (listing) => {
  const parts = [];
  if (listing.beds) parts.push(`${listing.beds} Bed`);
  if (listing.baths) parts.push(`${listing.baths} Bath`);
  const sqft = formatSqft(listing);
  if (sqft) parts.push(sqft);
  return parts.join(' \u2022 ');
};

const LiveListingCard = ({ listing, index }) => (
  <Card
    data-testid={`listing-card-${listing.id}`}
    className="border border-gray-200 bg-white rounded-none shadow-sm overflow-hidden hover:shadow-xl transition-shadow animate-fade-in-up flex flex-col"
    style={{ animationDelay: `${index * 100}ms` }}
  >
    {listing.photos && listing.photos.length > 0 ? (
      <ImageWithSkeleton
        src={listing.photos[0]}
        alt={listing.name}
        aspectRatio="aspect-[4/3]"
        objectFit="object-cover"
      />
    ) : (
      <PhotoPlaceholder aspectRatio="aspect-[4/3]" />
    )}
    <CardContent className="p-6 flex flex-col flex-1">
      <h3 className="text-xl font-semibold text-black mb-2" data-testid={`listing-name-${listing.id}`}>
        {listing.name}
      </h3>
      <p className="text-gray-600 text-base mb-4">{bedBathLabel(listing) || 'Apartment'}</p>

      {listing.description && (
        <p className="text-gray-500 text-sm mb-6 line-clamp-3">{listing.description}</p>
      )}

      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-200 mt-auto">
        <div>
          <p className="text-sm text-gray-500 mb-1 font-medium">Rent</p>
          <p className="text-xl font-bold text-black" data-testid={`listing-rent-${listing.id}`}>
            {formatRent(listing)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500 mb-1 font-medium">Available</p>
          <p className="text-base font-medium text-black" data-testid={`listing-availability-${listing.id}`}>
            {formatDate(listing.availabilityDate) || 'Contact Us'}
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <Link
          to={`/floor-plans/${listing.id}`}
          data-testid={`listing-details-link-${listing.id}`}
          className="inline-flex items-center text-sm font-bold text-[#FF5722] uppercase tracking-wide hover:text-black transition-colors group"
        >
          VIEW DETAILS
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </CardContent>
  </Card>
);

const FallbackCard = ({ plan, index }) => (
  <Card
    data-testid={`fallback-card-${plan.id}`}
    className="border border-gray-200 bg-white rounded-none shadow-sm overflow-hidden hover:shadow-xl transition-shadow animate-fade-in-up"
    style={{ animationDelay: `${index * 100}ms` }}
  >
    <ImageWithSkeleton src={plan.image} alt={plan.title} aspectRatio="aspect-[4/3]" objectFit="object-cover" />
    <CardContent className="p-6">
      <h3 className="text-xl font-semibold text-black mb-2">{plan.title}</h3>
      <p className="text-gray-600 text-base mb-6">{plan.subtitle}</p>
      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-200">
        <div>
          <p className="text-sm text-gray-500 mb-1 font-medium">Price</p>
          <p className="text-xl font-bold text-black">{plan.price}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500 mb-1 font-medium">Available</p>
          <p className="text-base font-medium text-black">{plan.availability}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const SkeletonCard = () => (
  <Card className="border border-gray-200 bg-white rounded-none shadow-sm overflow-hidden">
    <div className="aspect-[4/3] bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer bg-[length:200%_100%]" />
    <CardContent className="p-6 space-y-4">
      <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse" />
      <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
      <div className="h-10 w-full bg-gray-100 rounded animate-pulse" />
    </CardContent>
  </Card>
);

const FloorPlans = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/listings`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        const data = await res.json();
        setListings(Array.isArray(data.listings) ? data.listings : []);
      } catch (e) {
        setListings([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, []);

  const hasLive = listings.length > 0;

  const [selectedBed, setSelectedBed] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  const bedOptions = useMemo(() => {
    const set = new Set();
    listings.forEach((l) => {
      if (l.beds !== null && l.beds !== undefined && l.beds !== '') {
        const n = parseInt(l.beds, 10);
        if (!Number.isNaN(n)) set.add(n);
      }
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [listings]);

  const visibleListings = useMemo(() => {
    let arr = listings;
    if (selectedBed !== 'all') {
      arr = arr.filter((l) => parseInt(l.beds, 10) === selectedBed);
    }
    if (sortBy !== 'default') {
      arr = [...arr].sort((a, b) => {
        const pa = priceOf(a);
        const pb = priceOf(b);
        if (pa === null && pb === null) return 0;
        if (pa === null) return 1;
        if (pb === null) return -1;
        return sortBy === 'price-asc' ? pa - pb : pb - pa;
      });
    }
    return arr;
  }, [listings, selectedBed, sortBy]);

  const pillClass = (active) =>
    `px-4 py-2 text-sm font-semibold uppercase tracking-wide rounded-full border transition-colors ${
      active
        ? 'bg-[#FF5722] text-white border-[#FF5722]'
        : 'bg-white text-gray-700 border-gray-300 hover:border-[#FF5722] hover:text-[#FF5722]'
    }`;

  return (
    <div className="min-h-screen bg-white" data-testid="floorplans-page">
      {/* Hero Section */}
      <div className="relative w-full h-[400px] md:h-[500px] mb-12">
        <img
          src="https://customer-assets.emergentagent.com/job_cross-domain-deploy/artifacts/6un646s0_shoootin-photo-4-46a125f58792aa5cb7e1ce26b1a9011c.jpg"
          alt="Modern bedroom"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white text-center px-4 animate-fade-in">
            FLOORPLANS
          </h1>
        </div>
      </div>

      {/* Header Section */}
      <div className="container mx-auto px-4 mb-12">
        <div className="text-center max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-up">
            FIND YOUR <span className="text-[#FF5722]">HOME</span>
          </h2>
          <p className="text-lg md:text-xl text-black leading-relaxed animate-fade-in-up animation-delay-100">
            Choose from brand new studio, 1-, 2-, and 3-bedroom apartments. Every layout includes spacious closets, gourmet kitchens, stainless steel appliances, tons of windows, bright living spaces, and modern bathrooms.
          </p>
        </div>
      </div>

      {/* Floor Plans Grid */}
      <div className="container mx-auto px-4 py-8">
        {/* Filter & Sort Bar */}
        {!loading && hasLive && (
          <div
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8"
            data-testid="floorplans-filter-bar"
          >
            <div className="flex flex-wrap gap-2" data-testid="bedroom-filters">
              <button
                type="button"
                onClick={() => setSelectedBed('all')}
                data-testid="filter-bed-all"
                className={pillClass(selectedBed === 'all')}
              >
                All
              </button>
              {bedOptions.map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setSelectedBed(n)}
                  data-testid={`filter-bed-${n}`}
                  className={pillClass(selectedBed === n)}
                >
                  {bedFilterLabel(n)}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="text-sm text-gray-500 font-medium whitespace-nowrap">
                Sort by
              </label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-select"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-black bg-white focus:outline-none focus:border-[#FF5722] cursor-pointer"
              >
                <option value="default">Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          </div>
        )}

        {/* No results after filtering */}
        {!loading && hasLive && visibleListings.length === 0 && (
          <div className="text-center py-16" data-testid="no-results">
            <p className="text-lg text-gray-600 mb-4">
              No {selectedBed === 'all' ? '' : `${bedFilterLabel(selectedBed).toLowerCase()} `}apartments are available right now.
            </p>
            <button
              type="button"
              onClick={() => setSelectedBed('all')}
              className="text-sm font-bold text-[#FF5722] uppercase tracking-wide hover:text-black transition-colors"
            >
              View all floor plans
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {loading && [1, 2, 3].map((n) => <SkeletonCard key={n} />)}

          {!loading && hasLive &&
            visibleListings.map((listing, index) => (
              <LiveListingCard key={listing.id} listing={listing} index={index} />
            ))}

          {!loading && !hasLive &&
            fallbackPlans.map((plan, index) => (
              <FallbackCard key={plan.id} plan={plan} index={index} />
            ))}

          {/* Apply Now Button in Grid (Hidden on Mobile) */}
          {!loading && (visibleListings.length > 0 || !hasLive) && (
            <div className="hidden lg:flex items-center justify-center">
              <a
                href={APPLY_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="apply-now-grid-button"
                className="inline-flex items-center justify-center bg-[#FF5722] text-white border-2 border-[#FF5722] hover:bg-white hover:text-[#FF5722] w-full h-auto py-3 px-6 text-base font-semibold uppercase transition-all duration-200 rounded-md"
              >
                APPLY NOW
              </a>
            </div>
          )}
        </div>

        {/* Apply Now Button (Mobile Only) */}
        <div className="lg:hidden text-center mt-8">
          <a
            href={APPLY_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="apply-now-mobile-button"
            className="inline-flex items-center justify-center bg-[#FF5722] text-white border-2 border-[#FF5722] hover:bg-white hover:text-[#FF5722] w-full md:w-auto py-3 px-8 text-base font-semibold uppercase transition-all duration-200 rounded-md"
          >
            APPLY NOW
          </a>
        </div>
      </div>
    </div>
  );
};

export default FloorPlans;
